<template>
  <BaseModal :modalTitle="$t('W: Tastaturgenveje')">
    <div class="flex flex-col gap-5">
      <!-- Global -->
      <section>
        <h3 class="mb-2 text-xs font-semibold tracking-wide text-text-secondary uppercase">
          {{ $t('W: Global') }}
        </h3>
        <div class="flex flex-col gap-0.5">
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Åbn søgning') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ metaShortcut('K') }}</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Naviger til menupunkt 1–{n}', { n: navItemCount }) }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ altShortcut('1') }}</kbd>
              <span class="text-xs text-text-secondary">…</span>
              <kbd class="shortcut-key">{{ altShortcut(String(navItemCount)) }}</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Vis tastaturgenveje') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">?</kbd>
            </div>
          </div>
          <div v-if="!isLabAdminUser" class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Åbn profil') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ altShortcut('P') }}</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Opret ny (hvis muligt på siden)') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ altShortcut('C') }}</kbd>
            </div>
          </div>
        </div>
      </section>

      <!-- Tables -->
      <section>
        <h3 class="mb-2 text-xs font-semibold tracking-wide text-text-secondary uppercase">
          {{ $t('W: I tabeller') }}
        </h3>
        <div class="flex flex-col gap-0.5">
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Fokusér søgning') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ altShortcut('S') }}</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Scroll vandret') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">Shift</kbd>
              <span class="text-xs text-text-secondary">+</span>
              <span class="text-xs text-text-secondary">{{ $t('W: scroll') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Contextual -->
      <section>
        <h3 class="mb-2 text-xs font-semibold tracking-wide text-text-secondary uppercase">
          {{ $t('W: Kontekstafhængig') }}
        </h3>
        <div class="flex flex-col gap-0.5">
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Send kommentar / indsend formular') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ metaShortcut('Enter') }}</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Luk panel, modal eller søgning') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">Esc</kbd>
            </div>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-label">{{ $t('W: Hop mellem grupper i søgning') }}</span>
            <div class="shortcut-keys">
              <kbd class="shortcut-key">{{ metaShortcut('↑') }}</kbd>
              <kbd class="shortcut-key">{{ metaShortcut('↓') }}</kbd>
            </div>
          </div>
        </div>
      </section>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import { useNavItems } from '@/composables/useNavItems'
import { useUserStore } from '@/stores/userStore'
import { altShortcut, metaShortcut } from '@/globals/typescript/utils/platform'
import { mapState } from 'pinia'

export default {
  name: 'KeyboardShortcutsModal',

  setup() {
    return { navItems: useNavItems() }
  },

  computed: {
    ...mapState(useUserStore, ['isLabAdminUser']),
    navItemCount(): number {
      return this.navItems.length
    },
    altShortcut(): typeof altShortcut {
      return altShortcut
    },
    metaShortcut(): typeof metaShortcut {
      return metaShortcut
    },
  },
}
</script>

<style scoped>
.shortcut-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-radius: 0.375rem;
  padding: 0.375rem 0.5rem;
}

.shortcut-row:hover {
  background: var(--color-surface-muted);
}

.shortcut-label {
  font-size: 0.875rem;
  color: var(--color-text-primary);
}

.shortcut-keys {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.25rem;
}

.shortcut-key {
  border-radius: 0.25rem;
  border: 1px solid var(--color-border-subtle);
  padding: 0.125rem 0.375rem;
  font-family: sans-serif;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--color-text-secondary);
  background: var(--color-surface-muted);
  white-space: nowrap;
}
</style>
